/* ==========================================================================
   Scirocco — sections.js
   JS delle sezioni di contenuto (caricato su ogni pagina). Usa le API di theme.js
   (window.SC) e non fa nulla se i suoi elementi non ci sono.

   - <sc-hero>   : Ken Burns dopo lo splash, video facoltativo (play/pausa, fuori
                   schermo si mette in pausa, con "Riduci movimento" non parte).
   - <sc-ugc>    : slider community con frecce e deriva lenta automatica (con pausa/riproduci).
   - [data-sc-countup] : numeri che contano da 0 quando entrano in pagina.
   ========================================================================== */
(() => {
  'use strict';

  const SC = (window.SC = window.SC || {});
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const animOk = () => (SC.motion && SC.motion.enabled ? SC.motion.enabled() : true) && !reduced();
  const saveData = () => !!(navigator.connection && navigator.connection.saveData);

  /* ---------- numeri che contano ---------- */
  const countObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries, obs) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              obs.unobserve(entry.target);
              runCountUp(entry.target);
            });
          },
          { rootMargin: '0px 0px -10% 0px', threshold: 0.4 }
        )
      : null;

  function runCountUp(el) {
    const raw = (el.textContent || '').trim();
    const parts = raw.match(/^(\D*)(\d+)([\s\S]*)$/);
    if (!parts) return;
    const target = parseInt(parts[2], 10);
    if (!target || target > 100000) return;
    const prefix = parts[1];
    const suffix = parts[3];
    const duration = Math.min(1200, 380 + target * 10);
    const start = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = prefix + Math.round(target * eased) + suffix;
      if (p < 1) requestAnimationFrame(step);
      else el.textContent = raw;
    };
    requestAnimationFrame(step);
  }

  function scanCountUp(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('[data-sc-countup]').forEach((el) => {
      if (el.dataset.scCountBound === '1') return;
      el.dataset.scCountBound = '1';
      if (!animOk() || !countObserver) return;
      countObserver.observe(el);
    });
  }

  /* ---------- hero ---------- */
  class ScHero extends HTMLElement {
    connectedCallback() {
      this.hero = this.classList.contains('sc-hero') ? this : this.querySelector('.sc-hero');
      this.wrap = this.querySelector('[data-sc-hero-video]');
      this.video = this.wrap ? this.wrap.querySelector('video') : null;
      this.toggle = this.querySelector('[data-sc-hero-video-toggle]');
      this.userPaused = false;

      // Ken Burns solo dopo lo splash (e solo se le animazioni sono attive)
      const ready = () => this.hero && this.hero.classList.add('is-ready');
      if (SC.ready && SC.ready.splash && animOk()) SC.ready.splash.then(() => requestAnimationFrame(ready));

      if (!this.video) return;

      // listener legati a questa connessione: disconnectedCallback li toglie tutti con abort()
      // (nell'editor una sezione riordinata si stacca e si riattacca)
      this.listeners = new AbortController();
      const signal = this.listeners.signal;

      this.video.muted = true;
      this.video.setAttribute('muted', '');
      this.onPlaying = () => {
        if (this.wrap) this.wrap.classList.add('is-playing');
        this.setToggle(true);
      };
      this.video.addEventListener('playing', this.onPlaying, { signal });
      this.video.addEventListener('pause', () => this.setToggle(false), { signal });

      if (this.toggle) {
        this.onToggle = () => {
          if (this.video.paused) {
            this.userPaused = false;
            this.play();
          } else {
            this.userPaused = true;
            this.video.pause();
          }
        };
        this.toggle.addEventListener('click', this.onToggle, { signal });
      }

      // niente autoplay con "Riduci movimento" o risparmio dati: resta la foto
      if (reduced() || saveData()) {
        this.setToggle(false);
        return;
      }

      // parte quando è visibile; fuori schermo si ferma (batteria e CPU)
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                if (!this.userPaused) this.play();
              } else if (!this.video.paused) {
                this.video.pause();
              }
            });
          },
          { threshold: 0.15 }
        );
        this.io.observe(this);
      } else {
        this.play();
      }

      this.onVisibility = () => {
        if (document.hidden) this.video.pause();
        else if (!this.userPaused) this.play();
      };
      document.addEventListener('visibilitychange', this.onVisibility, { signal });
    }

    play() {
      if (!this.video) return;
      const p = this.video.play();
      if (p && typeof p.catch === 'function') p.catch(() => this.setToggle(false));
    }

    setToggle(playing) {
      if (!this.toggle) return;
      this.toggle.setAttribute('data-playing', playing ? 'true' : 'false');
      const label = playing ? this.toggle.dataset.labelPause : this.toggle.dataset.labelPlay;
      if (label) this.toggle.setAttribute('aria-label', label);
    }

    disconnectedCallback() {
      if (this.listeners) this.listeners.abort();
      if (this.io) this.io.disconnect();
      this.io = null;
    }
  }

  /* ---------- slider community ---------- */
  const DRIFT_SPEED = 0.022; // px per millisecondo (~22px/s): lenta, quasi impercettibile

  class ScUgc extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-sc-ugc="track"]');
      if (!this.track) return;
      this.prevBtn = this.querySelector('[data-sc-ugc="prev"]');
      this.nextBtn = this.querySelector('[data-sc-ugc="next"]');
      this.toggleBtn = this.querySelector('[data-sc-ugc="toggle"]');
      this.dir = 1;
      this.pos = this.track.scrollLeft;

      // listener legati a questa connessione: disconnectedCallback li toglie tutti con abort()
      // (nell'editor una sezione riordinata si stacca e si riattacca)
      this.listeners = new AbortController();
      const signal = this.listeners.signal;

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.nudge(-1), { signal });
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.nudge(1), { signal });

      this.onScroll = () => {
        if (this.scrollRaf) return;
        this.scrollRaf = requestAnimationFrame(() => {
          this.scrollRaf = 0;
          this.updateArrows();
        });
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true, signal });
      this.updateArrows();

      this.startDrift(signal);
    }

    step() {
      const item = this.track.firstElementChild;
      const w = item ? item.getBoundingClientRect().width : this.track.clientWidth * 0.6;
      return Math.round(w + 16);
    }

    nudge(dir) {
      this.holdDrift(2600);
      this.track.scrollBy({ left: dir * this.step(), behavior: reduced() ? 'auto' : 'smooth' });
    }

    updateArrows() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prevBtn) this.prevBtn.disabled = this.track.scrollLeft <= 2;
      if (this.nextBtn) this.nextBtn.disabled = this.track.scrollLeft >= max;
    }

    /* deriva automatica: solo desktop, animazioni attive, nessuna interazione in corso.
       Il ciclo rAF gira solo con la sezione in vista e la scheda del browser visibile;
       il pulsante pausa/riproduci la ferma del tutto (WCAG 2.2.2: si muove per più di 5 s). */
    startDrift(signal) {
      if (!this.hasAttribute('data-sc-drift')) return;
      if (!animOk() || saveData()) return;
      this.drifting = true;
      this.visible = false;
      this.hover = false;
      this.hasFocus = false;

      const hold = () => this.holdDrift(1400);
      this.track.addEventListener('pointerenter', () => (this.hover = true), { signal });
      this.track.addEventListener(
        'pointerleave',
        () => {
          this.hover = false;
          hold();
        },
        { signal }
      );
      this.track.addEventListener('pointerdown', hold, { signal });
      this.track.addEventListener('wheel', hold, { passive: true, signal });
      this.track.addEventListener('touchstart', hold, { passive: true, signal });
      // il focus sul pulsante pausa non ferma la deriva: "riproduci" deve ripartire subito
      this.addEventListener('focusin', (e) => (this.hasFocus = e.target !== this.toggleBtn), { signal });
      this.addEventListener(
        'focusout',
        () => {
          this.hasFocus = false;
          hold();
        },
        { signal }
      );

      if (this.toggleBtn) {
        this.toggleBtn.hidden = false;
        this.setToggle();
        this.toggleBtn.addEventListener(
          'click',
          () => {
            this.userPaused = !this.userPaused;
            this.setToggle();
            this.syncDrift();
          },
          { signal }
        );
      }

      // solo desktop (frecce e pulsante pausa si vedono da 990px): segue anche il ridimensionamento
      this.desktop = window.matchMedia('(min-width: 990px)');
      if (this.desktop.addEventListener) this.desktop.addEventListener('change', () => this.syncDrift(), { signal });
      document.addEventListener('visibilitychange', () => this.syncDrift(), { signal });

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((e) => (this.visible = e.isIntersecting));
            this.syncDrift();
          },
          { threshold: 0.2 }
        );
        this.io.observe(this);
      } else {
        this.visible = true;
      }

      this.loop = (now) => this.tick(now);
      this.syncDrift();
    }

    /** Avvia o ferma il ciclo rAF: gira solo se serve davvero. */
    syncDrift() {
      const run = this.drifting && this.visible && !this.userPaused && !document.hidden && this.desktop.matches;
      if (run && !this.raf) {
        this.last = 0;
        this.raf = requestAnimationFrame(this.loop);
      } else if (!run && this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = 0;
      }
    }

    setToggle() {
      if (!this.toggleBtn) return;
      const playing = !this.userPaused;
      this.toggleBtn.setAttribute('data-playing', playing ? 'true' : 'false');
      const label = playing ? this.toggleBtn.dataset.labelPause : this.toggleBtn.dataset.labelPlay;
      if (label) this.toggleBtn.setAttribute('aria-label', label);
    }

    holdDrift(ms) {
      this.holdUntil = performance.now() + ms;
    }

    tick(now) {
      this.raf = requestAnimationFrame(this.loop);
      const dt = this.last ? Math.min(50, now - this.last) : 0;
      this.last = now;
      if (!dt || this.hover || this.hasFocus) return;
      if (this.holdUntil && now < this.holdUntil) return;

      const max = this.track.scrollWidth - this.track.clientWidth;
      if (max < 8) return;
      // se l'utente ha scrollato a mano, ripartiamo da dove si trova
      if (Math.abs(this.track.scrollLeft - this.pos) > 2) this.pos = this.track.scrollLeft;

      this.pos += this.dir * DRIFT_SPEED * dt;
      if (this.pos >= max) {
        this.pos = max;
        this.dir = -1;
      } else if (this.pos <= 0) {
        this.pos = 0;
        this.dir = 1;
      }
      this.track.scrollLeft = this.pos;
    }

    disconnectedCallback() {
      if (this.listeners) this.listeners.abort();
      this.drifting = false;
      if (this.raf) cancelAnimationFrame(this.raf);
      if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
      this.raf = 0;
      this.scrollRaf = 0;
      if (this.io) this.io.disconnect();
      this.io = null;
    }
  }

  /* ---------- avvio ---------- */
  function boot() {
    if (SC.define) {
      SC.define('sc-hero', ScHero);
      SC.define('sc-ugc', ScUgc);
    } else {
      if (!customElements.get('sc-hero')) customElements.define('sc-hero', ScHero);
      if (!customElements.get('sc-ugc')) customElements.define('sc-ugc', ScUgc);
    }
    scanCountUp(document);
    document.addEventListener('sc:dom:updated', (e) => scanCountUp((e.detail && e.detail.root) || document));
    document.addEventListener('shopify:section:load', (e) => scanCountUp(e.target));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
